import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import {
  createSutraCommentary,
  deleteSutraCommentary,
  getSutraCommentary,
  listSutraCommentary,
  updateSutraCommentary,
  type SutraCommentary,
} from '../api/sutraCommentaryApi';

function errMessage(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = ax?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string') return msg;
  return ax?.message || 'Request failed';
}

export function SutraCommentaryPage() {
  const [rows, setRows] = useState<SutraCommentary[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SutraCommentary | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<SutraCommentary | null>(null);
  const [form] = Form.useForm<{ name: string }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listSutraCommentary());
    } catch (err: unknown) {
      message.error(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row: SutraCommentary) => {
    setEditing(row);
    form.setFieldsValue({ name: row.name });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const name = values.name.trim();
      if (editing) {
        if (name === editing.name) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateSutraCommentary(editing.id, { name });
        message.success('Commentary updated');
      } else {
        await createSutraCommentary({ name });
        message.success('Commentary created');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      setDetail(await getSutraCommentary(id));
    } catch (err: unknown) {
      message.error(errMessage(err));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const onDelete = async (id: number) => {
    try {
      await deleteSutraCommentary(id);
      message.success('Commentary deleted');
      await load();
    } catch (err: unknown) {
      message.error(errMessage(err));
    }
  };

  const columns: ColumnsType<SutraCommentary> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, row) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void openDetail(row.id)}>
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm title="Delete this commentary source?" onConfirm={() => void onDelete(row.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Sutra Commentary"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add commentary
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        CRUD for <code>/sutra-commentary</code>.
      </Typography.Paragraph>
      <Table<SutraCommentary>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 10, showSizeChanger: true }}
      />

      <Modal
        title="Commentary details"
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
        footer={null}
      >
        {detailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : detail ? (
          <Space direction="vertical">
            <Typography.Text>
              <strong>ID:</strong> {detail.id}
            </Typography.Text>
            <Typography.Text>
              <strong>Name:</strong> {detail.name}
            </Typography.Text>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={editing ? 'Edit commentary' : 'Add commentary'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submit()}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Name is required' },
              { validator: (_, v) => (String(v ?? '').trim() ? Promise.resolve() : Promise.reject(new Error('Name is required'))) },
            ]}
          >
            <Input placeholder="Kashika" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
